import axios from 'axios';
import { BACKEND_URL } from '../config_and_helpers/config';
import { logError, logInfo } from '../config_and_helpers/logger';
import { buildOcRequestListParams } from '../utils/ocRequest';

const LOG_CONTEXT = 'oc_request_client';

/**
 * @param {*} errPayload
 * @returns {string[]}
 */
export const formatOcRequestErrors = errPayload => {
  if (errPayload == null) return ['Request failed. Please try again.'];
  if (typeof errPayload === 'string') return [errPayload];
  if (Array.isArray(errPayload)) return errPayload.map(String);
  if (errPayload.detail) {
    return Array.isArray(errPayload.detail)
      ? errPayload.detail.map(String)
      : [String(errPayload.detail)];
  }
  const messages = [];
  Object.entries(errPayload).forEach(([key, value]) => {
    if (value == null) return;
    if (Array.isArray(value)) value.forEach(v => messages.push(`${key}: ${v}`));
    else messages.push(`${key}: ${String(value)}`);
  });
  return messages.length ? messages : ['Request failed. Please try again.'];
};

const fail = (error, op) => {
  const data = error.response?.data ?? error.message;
  logError(LOG_CONTEXT, `${op} failed`, data);
  return {
    success: false,
    status: error.response?.status,
    messages: formatOcRequestErrors(data),
    error: data,
  };
};

/** POST /oc-requests/public/ */
export const createPublicOcRequest = async payload => {
  try {
    logInfo(LOG_CONTEXT, 'createPublicOcRequest start');
    const response = await axios.post(`${BACKEND_URL}oc-requests/public/`, payload);
    logInfo(LOG_CONTEXT, 'createPublicOcRequest success', {
      requestNumber: response.data?.request_number,
    });
    return { success: true, data: response.data };
  } catch (error) {
    return fail(error, 'createPublicOcRequest');
  }
};

/** GET /oc-requests/public/{token}/ */
export const getPublicOcRequest = async token => {
  try {
    logInfo(LOG_CONTEXT, 'getPublicOcRequest start');
    const response = await axios.get(
      `${BACKEND_URL}oc-requests/public/${encodeURIComponent(token)}/`
    );
    return { success: true, data: response.data };
  } catch (error) {
    return fail(error, 'getPublicOcRequest');
  }
};

/** PATCH /oc-requests/public/{token}/ */
export const patchPublicOcRequest = async (token, body) => {
  try {
    logInfo(LOG_CONTEXT, 'patchPublicOcRequest start', { action: body?.action });
    const response = await axios.patch(
      `${BACKEND_URL}oc-requests/public/${encodeURIComponent(token)}/`,
      body
    );
    return { success: true, data: response.data };
  } catch (error) {
    return fail(error, 'patchPublicOcRequest');
  }
};

/** POST /oc-requests/public/{token}/attachments/ */
export const uploadPublicOcAttachment = async (token, { file, filename, s3Key, category }) => {
  try {
    logInfo(LOG_CONTEXT, 'uploadPublicOcAttachment start');
    const formData = new FormData();
    if (file) formData.append('file', file);
    if (filename) formData.append('filename', filename);
    if (s3Key) formData.append('s3_key', s3Key);
    if (category) formData.append('category', category);
    const response = await axios.post(
      `${BACKEND_URL}oc-requests/public/${encodeURIComponent(token)}/attachments/`,
      formData
    );
    return { success: true, data: response.data };
  } catch (error) {
    return fail(error, 'uploadPublicOcAttachment');
  }
};

/** GET /oc-requests/ */
export const getOcRequests = async (token, filters = {}) => {
  try {
    const params = buildOcRequestListParams(filters);
    const query = params.toString();
    const url = query
      ? `${BACKEND_URL}oc-requests/?${query}`
      : `${BACKEND_URL}oc-requests/`;
    logInfo(LOG_CONTEXT, 'getOcRequests start', { filters });
    const response = await axios.get(url, {
      headers: { Authorization: `Token ${token}` },
    });
    return {
      success: true,
      data: response.data,
      results: response.data?.results ?? response.data ?? [],
      next: response.data?.next ?? null,
    };
  } catch (error) {
    return fail(error, 'getOcRequests');
  }
};

/** GET /oc-requests/{id}/ */
export const getOcRequestDetail = async (token, requestId) => {
  try {
    logInfo(LOG_CONTEXT, 'getOcRequestDetail start', { requestId });
    const response = await axios.get(`${BACKEND_URL}oc-requests/${requestId}/`, {
      headers: { Authorization: `Token ${token}` },
    });
    return { success: true, data: response.data };
  } catch (error) {
    return fail(error, 'getOcRequestDetail');
  }
};

/** PATCH /oc-requests/{id}/ */
export const patchOcRequest = async (token, requestId, payload) => {
  try {
    logInfo(LOG_CONTEXT, 'patchOcRequest start', { requestId });
    const response = await axios.patch(
      `${BACKEND_URL}oc-requests/${requestId}/`,
      payload,
      { headers: { Authorization: `Token ${token}` } }
    );
    return { success: true, data: response.data };
  } catch (error) {
    return fail(error, 'patchOcRequest');
  }
};

/** POST /oc-requests/{id}/transition/ */
export const transitionOcRequest = async (token, requestId, body) => {
  try {
    logInfo(LOG_CONTEXT, 'transitionOcRequest start', {
      requestId,
      action: body?.action,
    });
    const response = await axios.post(
      `${BACKEND_URL}oc-requests/${requestId}/transition/`,
      body,
      { headers: { Authorization: `Token ${token}` } }
    );
    return { success: true, data: response.data };
  } catch (error) {
    return fail(error, 'transitionOcRequest');
  }
};

/** GET /oc-requests/{id}/candidates/ */
export const getOcCandidates = async (token, requestId) => {
  try {
    const response = await axios.get(
      `${BACKEND_URL}oc-requests/${requestId}/candidates/`,
      { headers: { Authorization: `Token ${token}` } }
    );
    return { success: true, data: response.data };
  } catch (error) {
    return fail(error, 'getOcCandidates');
  }
};

/** POST /oc-requests/{id}/candidates/ */
export const createOcCandidate = async (token, requestId, payload) => {
  try {
    logInfo(LOG_CONTEXT, 'createOcCandidate start', { requestId });
    const response = await axios.post(
      `${BACKEND_URL}oc-requests/${requestId}/candidates/`,
      payload,
      { headers: { Authorization: `Token ${token}` } }
    );
    return { success: true, data: response.data };
  } catch (error) {
    return fail(error, 'createOcCandidate');
  }
};

/** PATCH /oc-requests/{id}/candidates/{candidateId}/ */
export const patchOcCandidate = async (token, requestId, candidateId, payload) => {
  try {
    logInfo(LOG_CONTEXT, 'patchOcCandidate start', {
      requestId,
      candidateId,
      keys: payload ? Object.keys(payload) : [],
    });
    const response = await axios.patch(
      `${BACKEND_URL}oc-requests/${requestId}/candidates/${candidateId}/`,
      payload,
      { headers: { Authorization: `Token ${token}` } }
    );
    return { success: true, data: response.data };
  } catch (error) {
    return fail(error, 'patchOcCandidate');
  }
};

/** DELETE /oc-requests/{id}/candidates/{candidateId}/ */
export const deleteOcCandidate = async (token, requestId, candidateId) => {
  try {
    await axios.delete(
      `${BACKEND_URL}oc-requests/${requestId}/candidates/${candidateId}/`,
      { headers: { Authorization: `Token ${token}` } }
    );
    return { success: true };
  } catch (error) {
    return fail(error, 'deleteOcCandidate');
  }
};

/** GET technical-review */
export const getOcTechnicalReview = async (token, requestId, candidateId) => {
  try {
    const response = await axios.get(
      `${BACKEND_URL}oc-requests/${requestId}/candidates/${candidateId}/technical-review/`,
      { headers: { Authorization: `Token ${token}` } }
    );
    return { success: true, data: response.data };
  } catch (error) {
    return fail(error, 'getOcTechnicalReview');
  }
};

/** POST technical-review */
export const saveOcTechnicalReview = async (token, requestId, candidateId, payload) => {
  try {
    logInfo(LOG_CONTEXT, 'saveOcTechnicalReview start', {
      requestId,
      candidateId,
      decision: payload?.decision,
    });
    const response = await axios.post(
      `${BACKEND_URL}oc-requests/${requestId}/candidates/${candidateId}/technical-review/`,
      payload,
      { headers: { Authorization: `Token ${token}` } }
    );
    return { success: true, data: response.data };
  } catch (error) {
    return fail(error, 'saveOcTechnicalReview');
  }
};

/**
 * POST /oc-requests/{id}/candidates/{candidateId}/preparation/
 * Advances one accepted OC; returns full request detail (same shape as transition).
 *
 * @param {string} token
 * @param {string|number} requestId
 * @param {string|number} candidateId
 * @param {{ action: string }} body
 */
export const advanceOcCandidatePreparation = async (
  token,
  requestId,
  candidateId,
  body
) => {
  try {
    logInfo(LOG_CONTEXT, 'advanceOcCandidatePreparation start', {
      requestId,
      candidateId,
      action: body?.action,
    });
    const response = await axios.post(
      `${BACKEND_URL}oc-requests/${requestId}/candidates/${candidateId}/preparation/`,
      body,
      { headers: { Authorization: `Token ${token}` } }
    );
    logInfo(LOG_CONTEXT, 'advanceOcCandidatePreparation success', {
      requestId,
      candidateId,
      status: response.data?.status,
    });
    return { success: true, data: response.data };
  } catch (error) {
    return fail(error, 'advanceOcCandidatePreparation');
  }
};

/** POST comments */
export const createOcComment = async (token, requestId, { body, visibility = 'internal' }) => {
  try {
    const response = await axios.post(
      `${BACKEND_URL}oc-requests/${requestId}/comments/`,
      { body, visibility },
      { headers: { Authorization: `Token ${token}` } }
    );
    return { success: true, data: response.data };
  } catch (error) {
    return fail(error, 'createOcComment');
  }
};

/** POST attachments (internal) */
export const uploadOcAttachment = async (
  token,
  requestId,
  { file, filename, visibility = 'internal', category, candidate }
) => {
  try {
    const formData = new FormData();
    if (file) formData.append('file', file);
    if (filename) formData.append('filename', filename);
    formData.append('visibility', visibility);
    if (category) formData.append('category', category);
    if (candidate) formData.append('candidate', candidate);
    const response = await axios.post(
      `${BACKEND_URL}oc-requests/${requestId}/attachments/`,
      formData,
      { headers: { Authorization: `Token ${token}` } }
    );
    return { success: true, data: response.data };
  } catch (error) {
    return fail(error, 'uploadOcAttachment');
  }
};

/** GET export PDF */
export const exportOcRequestPdf = async (token, requestId) => {
  try {
    logInfo(LOG_CONTEXT, 'exportOcRequestPdf start', { requestId });
    const response = await axios.get(
      `${BACKEND_URL}oc-requests/${requestId}/export/`,
      {
        headers: { Authorization: `Token ${token}` },
        responseType: 'blob',
      }
    );
    return { success: true, data: response.data };
  } catch (error) {
    return fail(error, 'exportOcRequestPdf');
  }
};

/** GET /oc-requests/assignee-candidates/ */
export const getOcAssigneeCandidates = async token => {
  try {
    logInfo(LOG_CONTEXT, 'getOcAssigneeCandidates start');
    const response = await axios.get(
      `${BACKEND_URL}oc-requests/assignee-candidates/`,
      { headers: { Authorization: `Token ${token}` } }
    );
    const list = Array.isArray(response.data)
      ? response.data
      : response.data?.results ?? [];
    return { success: true, data: list };
  } catch (error) {
    return fail(error, 'getOcAssigneeCandidates');
  }
};

/** GET /oc-workflow-settings/ */
export const getOcWorkflowSettings = async token => {
  try {
    logInfo(LOG_CONTEXT, 'getOcWorkflowSettings start');
    const response = await axios.get(`${BACKEND_URL}oc-workflow-settings/`, {
      headers: { Authorization: `Token ${token}` },
    });
    return { success: true, data: response.data };
  } catch (error) {
    return fail(error, 'getOcWorkflowSettings');
  }
};

/**
 * PATCH /oc-workflow-settings/
 * @param {string} token
 * @param {Object} payload - default_*_id keys (null clears)
 */
export const patchOcWorkflowSettings = async (token, payload) => {
  try {
    logInfo(LOG_CONTEXT, 'patchOcWorkflowSettings start', {
      keys: Object.keys(payload || {}),
    });
    const response = await axios.patch(
      `${BACKEND_URL}oc-workflow-settings/`,
      payload,
      { headers: { Authorization: `Token ${token}` } }
    );
    return { success: true, data: response.data };
  } catch (error) {
    return fail(error, 'patchOcWorkflowSettings');
  }
};
